/** Plate-based V2 of the Application Form editor — thin config wrapper around
    the shared PlateDocumentDrawer. */
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { closeApplicationForm, setApplicationFormData } from '../../store/investor/action';
import { fetchApplicationFormTemplate } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { formatDateString } from '../../utils/dateUtils';
import { AICA_APPLICATION_FORM_SAVE } from '../../utils/constants/mixpanelEvents/investorEvents';
import { PlateDocumentDrawer, type BuildInitialTemplateArgs } from '../PlateDocumentEditor/PlateDocumentDrawer';

function ApplicationFormV2() {
    const dispatch = useAppDispatch();
    const applicationFormData = useAppSelector((state) => state.investor.applicationForm);
    const orgId = applicationFormData.orgId ?? '';
    const companyName = applicationFormData.companyName ?? '';

    const buildInitialTemplate = useCallback(
        ({ orgId, investorOrgId, emailId, companyName, companyMainImg, dispatch }: BuildInitialTemplateArgs) =>
            new Promise<any[]>((resolve, reject) => {
                dispatch(
                    fetchCompanyOverviewData({
                        assesseeOrgId: orgId,
                        investorOrgId,
                        onSuccess: async (data: OverviewData) => {
                            const template = await fetchApplicationFormTemplate({
                                orgId,
                                accountType: 'INVESTOR',
                                pnoId: investorOrgId,
                                primaryEmail: emailId,
                            });
                            resolve(
                                initializeTemplateWithMetadata(template || [], {
                                    pnoLogo: companyMainImg ?? '',
                                    companyName: companyName ?? '',
                                    companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                    userName: '',
                                    userDesignation: '',
                                    userEmail: '',
                                    currentDate: formatDateString(new Date(), DATE_FORMAT),
                                }),
                            );
                        },
                        onError: () => reject(new Error('company overview fetch failed')),
                    }),
                );
            }),
        [],
    );

    return (
        <PlateDocumentDrawer
            docType="APPLICATION_FORM"
            title="Application Form"
            fileNamePrefix="ApplicationForm"
            drawerId="application-form-plate"
            toolbarHostId="application-form-v2-toolbar-host"
            commentContainerId="application-form-comment-container"
            uploadSource="application-form"
            imageDocumentType="applicationForm"
            mixpanelSaveEvent={AICA_APPLICATION_FORM_SAVE}
            orgId={orgId}
            companyName={companyName}
            loading={!!applicationFormData.loading}
            onClose={() => dispatch(closeApplicationForm())}
            setLoading={(loading) => dispatch(setApplicationFormData({ loading }))}
            buildInitialTemplate={buildInitialTemplate}
        />
    );
}

export default ApplicationFormV2;
